import React, { FormEvent, useState } from 'react'
import { useForm, Link, router } from '@inertiajs/react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'
import { ColorPicker } from '@/components/shared/ColorPicker'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'

interface BoardData {
  id: number
  name: string
  slug: string
  color: string | null
  project: { id: number; name: string }
}

interface BoardAgentData {
  id: number
  role: 'admin' | 'member'
  agent: {
    id: number
    username: string
    agent_type: string
  }
  created_at: string
}

interface ProjectAgentData {
  agent: {
    id: number
    username: string
    agent_type: string
  }
  project_role: string
}

type AccessRole = 'admin' | 'member' | 'no_access'

interface Props {
  board: BoardData
  board_agents: BoardAgentData[]
  project_agents: ProjectAgentData[]
}

export default function Edit({ board, board_agents, project_agents }: Props) {
  const [deleteTarget, setDeleteTarget] = useState(false)

  const { data, setData, put, processing, errors } = useForm({
    board: {
      name: board.name,
      color: board.color || '',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    put(`/manage/boards/${board.id}`)
  }

  function handleDelete() {
    router.delete(`/manage/boards/${board.id}`)
    setDeleteTarget(false)
  }

  const boardAgentByAgentId: Record<number, BoardAgentData> = {}
  board_agents.forEach((ba) => {
    boardAgentByAgentId[ba.agent.id] = ba
  })

  function currentAccess(agentId: number): AccessRole {
    return (boardAgentByAgentId[agentId]?.role as AccessRole) || 'no_access'
  }

  function handleRoleChange(agentId: number, newRole: AccessRole) {
    const existing = boardAgentByAgentId[agentId]

    if (newRole === 'no_access') {
      if (existing) {
        router.delete(`/manage/boards/${board.id}/remove_member/${existing.id}`, {
          preserveScroll: true,
        })
      }
      return
    }

    if (!existing) {
      router.post(
        `/manage/boards/${board.id}/add_member`,
        { agent_id: agentId, role: newRole },
        { preserveScroll: true }
      )
      return
    }

    if (existing.role !== newRole) {
      router.put(
        `/manage/boards/${board.id}/update_member/${existing.id}`,
        { role: newRole },
        { preserveScroll: true }
      )
    }
  }

  return (
    <SidebarLayout section="manage" title={`Edit board: ${board.name}`}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 800,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              Project
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {board.project.name}
            </Typography>
          </Box>
          <TextField
            label="Name"
            fullWidth
            autoFocus
            value={data.board.name}
            onChange={(e) => setData('board', { ...data.board, name: e.target.value })}
            error={'board.name' in errors}
            helperText={(errors as Record<string, string>)['board.name']}
            sx={{ mb: 2.5 }}
          />
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Color
            </Typography>
            <ColorPicker
              value={data.board.color}
              onChange={(color: string) => setData('board', { ...data.board, color })}
            />
          </Box>
          <Button type="submit" variant="contained" fullWidth size="large" disabled={processing} sx={{ mb: 2 }}>
            Update board
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/boards" style={{ color: '#1a1a1a' }}>
              Back to boards
            </Link>
          </Box>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h6" sx={{ mb: 2 }}>
          Board access
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Choose access for each project member. Selecting "No access" removes the member from this board.
        </Typography>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>Type</TableCell>
                <TableCell sx={{ width: 180 }}>Access</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {project_agents.map((pa) => (
                <TableRow key={pa.agent.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {pa.agent.username}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={pa.agent.agent_type === 'human' ? 'Human' : 'Machine'}
                      size="small"
                      variant="outlined"
                      color={pa.agent.agent_type === 'machine' ? 'secondary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl size="small" fullWidth>
                      <Select
                        value={currentAccess(pa.agent.id)}
                        onChange={(e) => handleRoleChange(pa.agent.id, e.target.value as AccessRole)}
                        size="small"
                        inputProps={{ 'data-testid': `access-${pa.agent.username}` }}
                      >
                        <MenuItem value="admin">Admin</MenuItem>
                        <MenuItem value="member">Member</MenuItem>
                        <MenuItem value="no_access">No access</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                </TableRow>
              ))}
              {project_agents.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                      No project members.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 3 }} />
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Danger zone
          </Typography>
          <Button variant="outlined" color="error" fullWidth onClick={() => setDeleteTarget(true)}>
            Delete this board
          </Button>
        </Box>
      </Paper>

      <ConfirmDialog
        open={deleteTarget}
        title="Delete board"
        message={`Are you sure you want to delete board '${board.name}'? All tasks in this board will also be deleted.`}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(false)}
      />
    </SidebarLayout>
  )
}
